/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import React from 'react';

/**
 * Interface for quick order grid table header resources.
 */
export interface IQuickOrderGridTableHeaderResources {
    quickOrderGridProductNumberText: string;
    quickOrderGridProductText: string;
    quickOrderGridUnitPriceText: string;
    quickOrderGridUnitOfMeasureText: string;
    quickOrderGridQuantityText: string;
    quickOrderGridActionsText: string;
}

/**
 * Interface for quick order grid table props.
 */
export interface IQuickOrderGridTableHeaderProps extends IComponentProps {
    context: ICoreContext;
    className: string;

    isMobile: boolean;
    resources: IQuickOrderGridTableHeaderResources;
}

/**
 * Interface for quick order grid table header component.
 */
export interface IQuickOrderGridTableHeaderComponent extends IComponent<IQuickOrderGridTableHeaderProps> {}

/**
 * Get the quick order grid header cells for desktop.
 * @param props -- The quick order grid table header props.
 * @returns The list of cells for quick order grid header component in desktop version.
 */
const QuickOrderGridDesktopHeaderCellsComponent: React.FC<IQuickOrderGridTableHeaderProps> = (props: IQuickOrderGridTableHeaderProps) => {
    const { resources } = props;
    const columnWithNumberClassName = `${props.className}__row__column-with-number`;
    return (
        <>
            <th>
                <span className={classnames(`${props.className}__emptyColumn`)} />
            </th>
            <th>{resources.quickOrderGridProductNumberText}</th>
            <th>{resources.quickOrderGridProductText}</th>
            <th className={columnWithNumberClassName}>{resources.quickOrderGridUnitPriceText}</th>
            <th>{resources.quickOrderGridUnitOfMeasureText}</th>
            <th className={columnWithNumberClassName}>{resources.quickOrderGridQuantityText}</th>
            <th>{resources.quickOrderGridActionsText}</th>
        </>
    );
};

/**
 * Get the quick order grid header cells for mobile.
 * @param props -- The quick order grid table header props.
 * @returns The quick order grid header component in desktop version.
 */
const QuickOrderGridMobileHeaderCellsComponent: React.FC<IQuickOrderGridTableHeaderProps> = (props: IQuickOrderGridTableHeaderProps) => {
    const { resources } = props;
    return (
        <>
            <th className={classnames(`${props.className}__emptyColumn`)} />
            <th>{resources.quickOrderGridProductNumberText}</th>
            <th />
        </>
    );
};

/**
 * Quick order table header component.
 * @param props - Configuration of the component.
 * @returns React node with the header.
 */
export const QuickOrderGridTableHeaderComponent: React.FC<IQuickOrderGridTableHeaderProps> = (props: IQuickOrderGridTableHeaderProps) => {
    const rowClassName = `${props.className}__row`;

    let headerCells: JSX.Element;
    if (props.isMobile) {
        headerCells = <QuickOrderGridMobileHeaderCellsComponent {...props} />;
    } else {
        headerCells = <QuickOrderGridDesktopHeaderCellsComponent {...props} />;
    }

    return (
        <thead className={props.className}>
            <tr className={rowClassName}>{headerCells}</tr>
        </thead>
    );
};
